import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, deleteDoc, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import './SuperAdminPanel.css';

export default function SuperAdminPanel() {
  const { currentUser, userRole, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { showToast, confirm } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [calendars, setCalendars] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCalendars: 0,
    totalBookings: 0
  });
  // Redirect if not super admin
  useEffect(() => {
    if (userRole && userRole !== 'superAdmin') {
      navigate('/dashboard');
    }
  }, [userRole, navigate]);

  // Real-time listener for calendars
  useEffect(() => {
    if (userRole !== 'superAdmin') return;

    setLoading(true);
    const q = query(collection(db, 'calendars'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const calendarsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCalendars(calendarsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching calendars:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userRole]);

  // Real-time listener for users
  useEffect(() => {
    if (userRole !== 'superAdmin') return;

    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    }, (error) => {
      console.error('Error fetching users:', error);
    });

    return () => unsubscribe();
  }, [userRole]);

  // Real-time listener for bookings
  useEffect(() => {
    if (userRole !== 'superAdmin') return;

    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(bookingsList);
    }, (error) => {
      console.error('Error fetching bookings:', error);
    });

    return () => unsubscribe();
  }, [userRole]);

  // Calculate stats whenever data changes
  useEffect(() => {
    setStats({
      totalUsers: users.length,
      totalCalendars: calendars.length,
      totalBookings: bookings.length
    });
  }, [calendars, users, bookings]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }

  async function handleDeleteCalendar(calendarId) {
    const confirmed = await confirm('هل أنت متأكد من حذف هذا التقويم؟ سيتم حذف جميع الحجوزات المرتبطة به.');
    if (!confirmed) return;

    try {
      const calendar = calendars.find(c => c.id === calendarId);

      // Remove calendar from all members' calendars arrays and favoriteCalendars
      if (calendar?.members) {
        for (const memberId of calendar.members) {
          const user = users.find(u => u.id === memberId);
          if (user) {
            const updatedCalendars = (user.calendars || []).filter(id => id !== calendarId);
            const updatedFavorites = (user.favoriteCalendars || []).filter(id => id !== calendarId);
            await updateDoc(doc(db, 'users', memberId), {
              calendars: updatedCalendars,
              favoriteCalendars: updatedFavorites
            });
          }
        }
      }

      // Delete all bookings for this calendar
      const calendarBookings = bookings.filter(b => b.calendarId === calendarId);
      for (const booking of calendarBookings) {
        await deleteDoc(doc(db, 'bookings', booking.id));
      }

      // Delete the calendar
      await deleteDoc(doc(db, 'calendars', calendarId));

      showToast('تم حذف التقويم بنجاح', 'success');
    } catch (error) {
      console.error('Error deleting calendar:', error);
      showToast('حدث خطأ أثناء الحذف', 'error');
    }
  }

  async function handleDeleteBooking(bookingId) {
    const confirmed = await confirm('هل أنت متأكد من حذف هذا الحجز؟');
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'bookings', bookingId));
      showToast('تم حذف الحجز بنجاح', 'success');
    } catch (error) {
      console.error('Error deleting booking:', error);
      showToast('حدث خطأ أثناء الحذف', 'error');
    }
  }

  async function handlePromoteToSuperAdmin(userId) {
    const confirmed = await confirm('هل تريد ترقية هذا المستخدم إلى Super Admin؟ سيحصل على صلاحيات كاملة.');
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        role: 'superAdmin'
      });

      showToast('تم ترقية المستخدم إلى Super Admin بنجاح', 'success');
    } catch (error) {
      console.error('Error promoting user:', error);
      showToast('حدث خطأ أثناء الترقية', 'error');
    }
  }

  async function handleDemoteFromSuperAdmin(userId) {
    if (userId === currentUser.uid) {
      showToast('لا يمكنك إزالة صلاحياتك الخاصة', 'warning');
      return;
    }

    const superAdmins = users.filter(u => u.role === 'superAdmin');
    const originalSuperAdmin = superAdmins.reduce((oldest, current) => {
      if (!oldest) return current;
      return new Date(current.createdAt) < new Date(oldest.createdAt) ? current : oldest;
    }, null);

    if (originalSuperAdmin && userId === originalSuperAdmin.id) {
      showToast('لا يمكن إزالة صلاحيات Super Admin الأصلي', 'warning');
      return;
    }

    const confirmed = await confirm('هل تريد إزالة صلاحيات Super Admin من هذا المستخدم؟');
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        role: 'user'
      });

      showToast('تم إزالة صلاحيات Super Admin بنجاح', 'success');
    } catch (error) {
      console.error('Error demoting user:', error);
      showToast('حدث خطأ أثناء إزالة الصلاحيات', 'error');
    }
  }

  async function handleDeleteUser(userId) {
    if (userId === currentUser.uid) {
      showToast('لا يمكنك حذف حسابك الخاص', 'warning');
      return;
    }

    const user = users.find(u => u.id === userId);
    if (!user) return;

    const superAdmins = users.filter(u => u.role === 'superAdmin');
    const originalSuperAdmin = superAdmins.reduce((oldest, current) => {
      if (!oldest) return current;
      return new Date(current.createdAt) < new Date(oldest.createdAt) ? current : oldest;
    }, null);

    if (originalSuperAdmin && userId === originalSuperAdmin.id) {
      showToast('لا يمكن حذف Super Admin الأصلي', 'warning');
      return;
    }

    const confirmed = await confirm(`هل أنت متأكد من حذف المستخدم "${user.displayName}"؟ سيتم حذف جميع حجوزاته وإزالته من جميع التقاويم. لا يمكن التراجع عن هذا الإجراء.`);
    if (!confirmed) return;

    try {
      const userCalendars = calendars.filter(c => c.members?.includes(userId));

      for (const calendar of userCalendars) {
        const updatedMembers = calendar.members.filter(id => id !== userId);
        await updateDoc(doc(db, 'calendars', calendar.id), {
          members: updatedMembers
        });
      }

      const userBookings = bookings.filter(b => b.bookedBy === userId);
      for (const booking of userBookings) {
        await deleteDoc(doc(db, 'bookings', booking.id));
      }

      await deleteDoc(doc(db, 'users', userId));

      showToast('تم حذف المستخدم بنجاح', 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast('حدث خطأ أثناء حذف المستخدم', 'error');
    }
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري تحميل لوحة التحكم...</p>
      </div>
    );
  }

  return (
    <div className="super-admin-wrapper">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span>العودة</span>
          </button>
        </div>

        <div className="admin-header-center">
          <h1 className="admin-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            لوحة Super Admin
          </h1>
        </div>

        <div className="admin-header-right">
          <button className="icon-btn" onClick={toggleTheme}>
            {isDark ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            )}
          </button>
          <button className="icon-btn" onClick={handleLogout}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-icon users-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.totalUsers}</h3>
            <p className="stat-label">إجمالي المستخدمين</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon calendars-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.totalCalendars}</h3>
            <p className="stat-label">إجمالي التقاويم</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bookings-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.totalBookings}</h3>
            <p className="stat-label">إجمالي الحجوزات</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          نظرة عامة
        </button>
        <button
          className={`tab-btn ${activeTab === 'calendars' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendars')}
        >
          التقاويم ({calendars.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          المستخدمين ({users.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          الحجوزات ({bookings.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-section">
              <h2>نظرة عامة على النظام</h2>
              <div className="overview-grid">
                <div className="overview-item">
                  <span className="overview-label">إجمالي المستخدمين:</span>
                  <span className="overview-value">{stats.totalUsers}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">إجمالي التقاويم:</span>
                  <span className="overview-value">{stats.totalCalendars}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">إجمالي الحجوزات:</span>
                  <span className="overview-value">{stats.totalBookings}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">متوسط الأعضاء لكل تقويم:</span>
                  <span className="overview-value">
                    {calendars.length > 0
                      ? Math.round(calendars.reduce((sum, cal) => sum + ((cal.members || []).filter(mid => users.some(u => u.id === mid)).length), 0) / calendars.length)
                      : 0}
                  </span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">متوسط الحجوزات لكل تقويم:</span>
                  <span className="overview-value">
                    {calendars.length > 0 
                      ? Math.round(bookings.length / calendars.length)
                      : 0}
                  </span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Super Admins:</span>
                  <span className="overview-value">
                    {users.filter(u => u.role === 'superAdmin').length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

  {/* Calendars Tab */}
  {activeTab === 'calendars' && (
    <div className="calendars-tab">
      <h2>جميع التقاويم</h2>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>اسم التقويم</th>
              <th>رمز الدعوة</th>
              <th>المنشئ</th>
              <th>الأعضاء</th>
              <th>الحجوزات</th>
              <th>تاريخ الإنشاء</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {calendars.map(calendar => {
              const creator = users.find(u => u.id === calendar.createdBy);
              const calendarBookings = bookings.filter(b => b.calendarId === calendar.id);
              const existingMembers = (calendar.members || []).filter(memberId => users.some(u => u.id === memberId));

              return (
                <tr key={calendar.id}>
                  <td className="table-name">{calendar.name}</td>
                  <td className="table-code">{calendar.inviteCode}</td>
                  <td>{creator?.displayName || 'غير معروف'}</td>
                  <td>{existingMembers.length}</td>
                  <td>{calendarBookings.length}</td>
                  <td>{new Date(calendar.createdAt).toLocaleDateString('ar-SA')}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn view-btn-small"
                        onClick={() => navigate(`/calendar/${calendar.id}`)}
                        title="عرض"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button
                        className="action-btn delete-btn-small"
                        onClick={() => handleDeleteCalendar(calendar.id)}
                        title="حذف"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  )}

  {/* Users Tab */}
  {activeTab === 'users' && (() => {
    // Calculate original super admin once (in real-time as users list updates)
    const superAdmins = users.filter(u => u.role === 'superAdmin');
    const originalSuperAdmin = superAdmins.reduce((oldest, current) => {
      if (!oldest) return current;
      return new Date(current.createdAt) < new Date(oldest.createdAt) ? current : oldest;
    }, null);

    return (
      <div className="users-tab">
        <h2>جميع المستخدمين</h2>
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>البريد الإلكتروني</th>
                <th>الدور</th>
                <th>التقاويم</th>
                <th>تاريخ التسجيل</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const userCalendars = calendars.filter(c =>
                  c.members?.includes(user.id)
                );
                const isOriginalSuperAdmin = originalSuperAdmin && user.id === originalSuperAdmin.id;

              return (
                <tr key={user.id}>
                  <td className="table-name">{user.displayName}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.role === 'superAdmin' ? (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span className="role-badge super-admin-badge">Super Admin</span>
                        {isOriginalSuperAdmin && (
                          <span className="role-badge creator-badge" title="Super Admin الأصلي">أصلي</span>
                        )}
                      </div>
                    ) : (
                      <span className="role-badge user-badge">مستخدم</span>
                    )}
                  </td>
                  <td>{userCalendars.length}</td>
                  <td>{new Date(user.createdAt).toLocaleDateString('ar-SA')}</td>
                  <td>
                    <div className="table-actions">
                      {user.role !== 'superAdmin' ? (
                        <>
                          <button
                            className="action-btn promote-btn-small"
                            onClick={() => handlePromoteToSuperAdmin(user.id)}
                            title="ترقية إلى Super Admin"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                          </button>
                          {user.id !== currentUser.uid && (
                            <button
                              className="action-btn delete-btn-small"
                              onClick={() => handleDeleteUser(user.id)}
                              title="حذف المستخدم"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          )}
                        </>
                      ) : user.id !== currentUser.uid && !isOriginalSuperAdmin && (
                        <>
                          <button
                            className="action-btn demote-btn-small"
                            onClick={() => handleDemoteFromSuperAdmin(user.id)}
                            title="إزالة صلاحيات Super Admin"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>
                          <button
                            className="action-btn delete-btn-small"
                            onClick={() => handleDeleteUser(user.id)}
                            title="حذف المستخدم"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>
        </div>
      );
    })()
  }

  {/* Bookings Tab */}
  {activeTab === 'bookings' && (
    <div className="bookings-tab">
      <h2>جميع الحجوزات</h2>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>التقويم</th>
              <th>التاريخ</th>
              <th>الاسم</th>
              <th>محجوز بواسطة</th>
              <th>تاريخ الحجز</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(booking => {
              const calendar = calendars.find(c => c.id === booking.calendarId);
              const bookedBy = booking.bookedBy ? users.find(u => u.id === booking.bookedBy) : null;
              
              return (
                <tr key={booking.id}>
                  <td className="table-name">{calendar?.name || 'تقويم محذوف'}</td>
                  <td>{new Date(booking.date).toLocaleDateString('ar-SA')}</td>
                  <td>{booking.guestName}</td>
                  <td>{bookedBy?.displayName || 'ضيف'}</td>
                  <td>{new Date(booking.createdAt).toLocaleDateString('ar-SA')}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn delete-btn-small"
                        onClick={() => handleDeleteBooking(booking.id)}
                        title="حذف"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  )}
</div>
    </div>
  );
}